import { apiClient } from '@shared/utils/api-client';
import type {
  Product,
  ProductCardResponse,
  CreateProductDto,
  SetDiscountDto,
  SetPopularDto,
  SetHiddenDto,
  SetOutOfStockDto,
  SetNewDto,
  FlowerType,
  AssemblyType,
  Color,
  ProductRejectionTemplate,
} from '../types';

export const productsApi = {
  update: async (id: number, data: Partial<CreateProductDto>, files?: File[], deletedImageIds?: number[]): Promise<Product> => {
    const formData = new FormData();
    if (data.name) {
      formData.append('name', data.name);
    }
    if (data.description !== undefined) {
      formData.append('description', data.description);
    }
    if (data.price !== undefined) {
      formData.append('price', data.price.toString());
    }
    if (data.categoryIds && data.categoryIds.length > 0) {
      data.categoryIds.forEach((categoryId) => {
        formData.append('categoryIds', categoryId.toString());
      });
    }
    if (data.composition !== undefined) {
      const filledComposition = (Array.isArray(data.composition) ? data.composition : []).filter(
        (item) => item.value != null && String(item.value).trim() !== ''
      );
      formData.append('composition', JSON.stringify(filledComposition));
    }
    if (data.keywords !== undefined) {
      if (data.keywords.length > 0) {
        data.keywords.forEach((keyword) => {
          formData.append('keywords', keyword);
        });
      }
    }
    if (data.height !== undefined) {
      formData.append('height', data.height.toString());
    }
    if (data.width !== undefined) {
      formData.append('width', data.width.toString());
    }
    if (data.weight !== undefined) {
      formData.append('weight', data.weight.toString());
    }
    if (data.flowerCount !== undefined) {
      formData.append('flowerCount', data.flowerCount.toString());
    }
    if (data.isNotFlower !== undefined) {
      formData.append('isNotFlower', data.isNotFlower.toString());
    }
    if (data.flowerTypeIds !== undefined) {
      if (data.flowerTypeIds.length > 0) {
        data.flowerTypeIds.forEach((flowerTypeId) => {
          formData.append('flowerTypeIds', flowerTypeId.toString());
        });
      }
    }
    if (data.assemblyTypeId !== undefined) {
      formData.append('assemblyTypeId', data.assemblyTypeId !== null ? data.assemblyTypeId.toString() : '');
    }
    if (data.colorIds !== undefined) {
      if (data.colorIds.length > 0) {
        data.colorIds.forEach((colorId) => {
          formData.append('colorIds', colorId.toString());
        });
      }
    }
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('images', file);
      });
    }
    if (deletedImageIds && deletedImageIds.length > 0) {
      deletedImageIds.forEach((imageId) => {
        formData.append('deletedImageIds', imageId.toString());
      });
    }
    return apiClient.patch<Product>(`/products/${id}`, formData);
  },

  getAll: async (filters?: {
    categoryId?: number;
    colorIds?: number[];
    flowerTypeIds?: number[];
    assemblyTypeIds?: number[];
    minPrice?: number;
    maxPrice?: number;
    sortBy?: string;
    sellerId?: number;
    search?: string;
    moderation?: 'all' | 'pending' | 'approved' | 'rejected';
    ratings?: number[];
    badges?: Array<'promo' | 'sale' | 'popular' | 'new' | 'hidden' | 'oos'>;
    page?: number;
    limit?: number;
  }): Promise<ProductCardResponse[] | { products: ProductCardResponse[]; total: number }> => {
    if (!filters) {
      return apiClient.get<ProductCardResponse[]>('/products');
    }
    const params = new URLSearchParams();
    if (filters.categoryId) params.append('categoryId', filters.categoryId.toString());
    if (filters.colorIds && filters.colorIds.length > 0) {
      filters.colorIds.forEach(id => params.append('colorIds', id.toString()));
    }
    if (filters.flowerTypeIds && filters.flowerTypeIds.length > 0) {
      filters.flowerTypeIds.forEach(id => params.append('flowerTypeIds', id.toString()));
    }
    if (filters.assemblyTypeIds && filters.assemblyTypeIds.length > 0) {
      filters.assemblyTypeIds.forEach(id => params.append('assemblyTypeIds', id.toString()));
    }
    if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sellerId !== undefined) {
      params.append('sellerId', filters.sellerId.toString());
    }
    if (filters.search?.trim()) {
      params.append('search', filters.search.trim());
    }
    if (filters.moderation && filters.moderation !== 'all') {
      params.append('moderation', filters.moderation);
    }
    filters.ratings?.forEach((rating) => params.append('rating', String(rating)));
    filters.badges?.forEach((badge) => params.append('badge', badge));
    if (filters.page != null) {
      params.append('page', String(filters.page));
    }
    if (filters.limit != null) {
      params.append('limit', String(filters.limit));
    }
    const queryString = params.toString();
    const url = queryString ? `/products?${queryString}` : '/products';
    if (filters.page != null) {
      return apiClient.get<{ products: ProductCardResponse[]; total: number }>(url);
    }
    return apiClient.get<ProductCardResponse[]>(url);
  },

  getById: async (id: number): Promise<Product> => {
    return apiClient.get<Product>(`/products/${id}`);
  },

  getModerationPendingCount: async (): Promise<{ count: number }> => {
    return apiClient.get<{ count: number }>('/products/moderation/pending-count');
  },

  setApproval: async (id: number, approved: boolean, reason?: string): Promise<Product> => {
    const body: { approved: boolean; reason?: string } = { approved };
    if (approved === false && reason !== undefined) {
      body.reason = reason;
    }
    return apiClient.patch<Product>(`/products/${id}/approval`, body);
  },

  getRejectionTemplates: async (): Promise<ProductRejectionTemplate[]> => {
    return apiClient.get<ProductRejectionTemplate[]>('/product-rejection-templates');
  },

  createRejectionTemplate: async (data: { title: string; text: string }): Promise<ProductRejectionTemplate> => {
    return apiClient.post<ProductRejectionTemplate>('/product-rejection-templates', data);
  },

  deleteRejectionTemplate: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/product-rejection-templates/${id}`);
  },

  setDiscount: async (id: number, data: SetDiscountDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/discount`, data);
  },

  setFlag: async (id: number, type: string, value: boolean): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/${type}`, { value });
  },

  // Оставляем старые методы для обратной совместимости
  setPopular: async (id: number, data: SetPopularDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/popular`, { value: data.isPopular });
  },

  setHidden: async (id: number, data: SetHiddenDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/hidden`, { value: data.isHidden });
  },

  setOutOfStock: async (id: number, data: SetOutOfStockDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/out-of-stock`, { value: data.isOutOfStock });
  },

  setNew: async (id: number, data: SetNewDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/new`, { value: data.isNew });
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/products/${id}`);
  },

  getPopular: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/popular');
  },

  getHidden: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/hidden');
  },

  getDiscount: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/discount');
  },

  getOutOfStock: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/out-of-stock');
  },

  getNew: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/new');
  },

  getFlowerTypes: async (): Promise<FlowerType[]> => {
    return apiClient.get<FlowerType[]>('/products/options/flower-types');
  },

  getAssemblyTypes: async (): Promise<AssemblyType[]> => {
    return apiClient.get<AssemblyType[]>('/products/options/assembly-types');
  },

  getColors: async (): Promise<Color[]> => {
    return apiClient.get<Color[]>('/products/options/colors');
  },

  // Color methods
  createColor: async (data: { name: string; hexCode?: string }): Promise<Color> => {
    return apiClient.post<Color>('/products/options/colors', data);
  },

  updateColor: async (id: number, data: { name?: string; hexCode?: string }): Promise<Color> => {
    return apiClient.patch<Color>(`/products/options/colors/${id}`, data);
  },

  deleteColor: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/options/colors/${id}`);
  },

  // FlowerType methods
  createFlowerType: async (data: { name: string }): Promise<FlowerType> => {
    return apiClient.post<FlowerType>('/products/options/flower-types', data);
  },

  updateFlowerType: async (id: number, data: { name?: string }): Promise<FlowerType> => {
    return apiClient.patch<FlowerType>(`/products/options/flower-types/${id}`, data);
  },

  deleteFlowerType: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/options/flower-types/${id}`);
  },

  // AssemblyType methods
  createAssemblyType: async (data: { name: string }): Promise<AssemblyType> => {
    return apiClient.post<AssemblyType>('/products/options/assembly-types', data);
  },

  updateAssemblyType: async (id: number, data: { name?: string }): Promise<AssemblyType> => {
    return apiClient.patch<AssemblyType>(`/products/options/assembly-types/${id}`, data);
  },

  deleteAssemblyType: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/options/assembly-types/${id}`);
  },

  // Массовые операции
  bulkSetFlag: async (type: string, productIds: number[], value: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>(`/products/bulk/flag/${type}`, { productIds, value });
  },

  // Оставляем старые методы для обратной совместимости
  bulkSetPopular: async (productIds: number[], isPopular: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/popular', { productIds, value: isPopular });
  },

  bulkSetHidden: async (productIds: number[], isHidden: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/hidden', { productIds, value: isHidden });
  },

  bulkSetOutOfStock: async (productIds: number[], isOutOfStock: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/out-of-stock', { productIds, value: isOutOfStock });
  },

  bulkSetNew: async (productIds: number[], isNew: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/new', { productIds, value: isNew });
  },

  bulkDelete: async (productIds: number[]): Promise<void> => {
    return apiClient.post<void>('/products/bulk/delete', { productIds });
  },

  bulkAddToCategory: async (productIds: number[], categoryId: number): Promise<{ success: boolean; count: number }> => {
    return apiClient.post<{ success: boolean; count: number }>('/products/bulk/add-to-category', { productIds, categoryId });
  },

  removeFromCategory: async (productId: number, categoryId: number): Promise<{ success: boolean }> => {
    return apiClient.delete<{ success: boolean }>(`/products/${productId}/category/${categoryId}`);
  },

  updateProductImagesOrder: async (productId: number, items: { id: number; order: number }[]): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${productId}/images/order`, { items });
  },

  createVariant: async (data: { products: Array<{ productId: number }> }): Promise<{ success: boolean; variantId: number; message: string }> => {
    return apiClient.post<{ success: boolean; variantId: number; message: string }>('/products/variants', data);
  },

  updateVariantName: async (productId: number, variantName: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.patch<{ success: boolean; message: string }>(`/products/${productId}/variant-name`, { variantName });
  },

  bulkUpdateVariantName: async (updates: Array<{ productId: number; variantName: string }>): Promise<{ success: boolean; count: number; total: number; results: Array<{ productId: number; success: boolean; message: string }>; message: string }> => {
    return apiClient.post<{ success: boolean; count: number; total: number; results: Array<{ productId: number; success: boolean; message: string }>; message: string }>('/products/variants/bulk-update-variant-name', { updates });
  },

  removeVariant: async (variantId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(`/products/variants/remove/${variantId}`);
  },

  disbandVariantGroup: async (productId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(`/products/${productId}/variant-group`);
  },

  /** Тест: запуск cron синхронизации isPopular по purchaseCount (только SUPER_ADMIN). */
  runSyncPopularByPurchaseCron: async (): Promise<{ markedPopular: number }> => {
    return apiClient.post<{ markedPopular: number }>('/products/cron/sync-popular-by-purchase');
  },

  /** Тест: скрыть товары не в наличии дольше N дней (только SUPER_ADMIN). */
  runHideLongOutOfStockCron: async (): Promise<{ hidden: number }> => {
    return apiClient.post<{ hidden: number }>('/products/cron/hide-long-out-of-stock');
  },

  /** Тест: пересчёт предрасчитанного ранга по категориям + глобальный ранг каталога (как cron). Только SUPER_ADMIN. */
  runRecomputeCategoryRankingCron: async (): Promise<{
    categories: number;
    links: number;
    globalProducts: number;
  }> => {
    return apiClient.post<{
      categories: number;
      links: number;
      globalProducts: number;
    }>(
      '/products/cron/recompute-category-ranking',
    );
  },
};

